import { useCallback, useEffect, useRef } from 'react';

import { ModelUpdatedEvent } from 'ag-grid-community';

import { SelectionService } from '../../utils/selectionService';

type UseGridSelectionRestoreParams<T> = {
  embryos: T[] | undefined;
  getSelectedIds: (
    selection: ReturnType<typeof SelectionService.getSelection>,
  ) => string[];
  getNodeId: (node: { data: any }) => string | undefined;
  onSelectionChanged: () => void;
};

export const useGridSelectionRestore = <T>({
  embryos,
  getSelectedIds,
  getNodeId,
  onSelectionChanged,
}: UseGridSelectionRestoreParams<T>) => {
  const isRestoringSelectionRef = useRef(false);
  const prevEmbryosLengthRef = useRef(embryos?.length ?? 0);
  const needsRestoreRef = useRef(false);

  useEffect(() => {
    const currentLength = embryos?.length ?? 0;
    const prevLength = prevEmbryosLengthRef.current;
    prevEmbryosLengthRef.current = currentLength;

    if (currentLength === 0) return;

    if (currentLength !== prevLength || prevLength === 0) {
      needsRestoreRef.current = true;
    }
  }, [embryos]);

  const handleModelUpdated = useCallback(
    (params: ModelUpdatedEvent) => {
      if (!needsRestoreRef.current) return;

      isRestoringSelectionRef.current = true;
      const selection = SelectionService.getSelection();
      const selectedIds = getSelectedIds(selection);

      params.api.forEachNode((node) => {
        const nodeId = getNodeId(node);
        const isSelected = nodeId !== undefined && selectedIds.includes(nodeId);
        node.setSelected(isSelected);
      });

      needsRestoreRef.current = false;
      setTimeout(() => {
        isRestoringSelectionRef.current = false;
      }, 0);
    },
    [getSelectedIds, getNodeId],
  );

  const wrappedSelectionChanged = useCallback(() => {
    if (isRestoringSelectionRef.current) {
      return;
    }
    onSelectionChanged();
  }, [onSelectionChanged]);

  return {
    wrappedSelectionChanged,
    onModelUpdated: handleModelUpdated,
  };
};
